import React, { useEffect, useRef, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import CustomersAPI, { unwrapItem } from '../../../api/v3/customers-api-v3'
import {
  fetchProvinces,
  fetchCities,
  fetchDistricts,
  fetchVillages,
  fetchPostalCodesByVillage,
} from '../../../api/v3/wilayah-api-v3'

const BASE = '/app/sales/bagspa'

// Default address fields
const INIT_ADDRESS = {
  street: '',
  subdistrict: '',
  district: '',
  city: '',
  province: '',
  postal_code: '',
  recipient_name: '',
  telephone: '',
  alias: '',
  is_customer_default: true,
  is_seller_default: false,
}

// Phone country dropdown
const PHONE_COUNTRIES = [
  { code: '+62', label: 'Indonesia (+62)' },
  { code: '+63', label: 'Philippines (+63)' },
  { code: '+65', label: 'Singapore (+65)' },
  { code: '+60', label: 'Malaysia (+60)' },
  { code: '+1', label: 'USA/Canada (+1)' },
]

// ---- phone helpers ----
function normalizeLocalPhone(local = '') {
  let l = (local || '').replace(/\D/g, '')
  if (l.startsWith('0')) l = l.slice(1)
  return l
}

function combinePhone(countryCode = '+62', local = '') {
  const raw = (local || '').trim()
  if (raw.startsWith('+')) return raw
  return `${countryCode}${normalizeLocalPhone(raw)}`
}

function splitPhoneIfE164(phone = '') {
  if (!phone?.startsWith('+')) return { country: '+62', local: phone || '' }
  const known = PHONE_COUNTRIES.map(c => c.code).sort((a, b) => b.length - a.length)
  const match = known.find(cc => phone.startsWith(cc))
  if (!match) return { country: '+62', local: phone }
  return { country: match, local: phone.slice(match.length) }
}

// ---- validation ----
function validateCustomer({ name, email, phone }) {
  const errors = []
  const words = String(name || '').trim().split(/\s+/).filter(Boolean)

  if (words.length < 2) errors.push('Name (min 2 words)')
  if (!email?.trim()) errors.push('Email')
  if (!phone?.trim()) errors.push('Phone')
  if (email && !/.+@.+\..+/.test(email)) errors.push('Email (format)')

  return errors
}

// ---- util ----
function findByName(list, nameKey, target) {
  if (!target) return null
  const needle = String(target).toLowerCase()
  return list.find(o => String(o[nameKey]).toLowerCase() === needle) || null
}

function hasAnyAddressValue(address) {
  return [
    address.street,
    address.subdistrict,
    address.district,
    address.city,
    address.province,
    address.postal_code,
    address.recipient_name,
    address.telephone,
    address.alias,
  ].some(v => String(v || '').trim() !== '')
}

export default function CustomerFormPage() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const history = useHistory()
  const formRef = useRef(null)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneCountry, setPhoneCountry] = useState('+62')

  const [address, setAddress] = useState(INIT_ADDRESS)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(isEdit)

  // region lists & selections
  const [provinces, setProvinces] = useState([])       // [{id, province}]
  const [cities, setCities] = useState([])             // [{id, name}]
  const [districts, setDistricts] = useState([])       // [{id, name}]
  const [subdistricts, setSubdistricts] = useState([]) // [{id, name}]
  const [postalCodes, setPostalCodes] = useState([])   // ['12250', ...]

  const [provinceId, setProvinceId] = useState('')
  const [cityId, setCityId] = useState('')
  const [districtId, setDistrictId] = useState('')
  const [subdistrictId, setSubdistrictId] = useState('')

  const [regionSourceFailed, setRegionSourceFailed] = useState(false)

  function updateAddressField(key, value) {
    setAddress(prev => ({ ...prev, [key]: value }))
  }

  // Load provinces immediately
  useEffect(() => {
    fetchProvinces()
      .then((items) => {
        setProvinces(Array.isArray(items) ? items : [])
        setRegionSourceFailed(false)
      })
      .catch(() => {
        setProvinces([])
        setRegionSourceFailed(true)
      })
  }, [])

  // Load cities on province change
  useEffect(() => {
    if (!provinceId) {
      setCities([])
      setCityId('')
      return
    }

    fetchCities(provinceId)
      .then((items) => setCities(Array.isArray(items) ? items : []))
      .catch(() => setCities([]))
  }, [provinceId])

  // Load districts on city change
  useEffect(() => {
    if (!cityId) {
      setDistricts([])
      setDistrictId('')
      return
    }

    fetchDistricts(cityId)
      .then((items) => setDistricts(Array.isArray(items) ? items : []))
      .catch(() => setDistricts([]))
  }, [cityId])

  // Load villages on district change
  useEffect(() => {
    if (!districtId) {
      setSubdistricts([])
      setSubdistrictId('')
      return
    }

    fetchVillages(districtId)
      .then((items) => setSubdistricts(Array.isArray(items) ? items : []))
      .catch(() => setSubdistricts([]))
  }, [districtId])

  // Reflect selection IDs -> address text
  useEffect(() => {
    const provName = provinces.find(p => String(p.id) === String(provinceId))?.province || ''
    const cityName = cities.find(c => String(c.id) === String(cityId))?.name || ''
    const distName = districts.find(d => String(d.id) === String(districtId))?.name || ''
    const subdName = subdistricts.find(v => String(v.id) === String(subdistrictId))?.name || ''

    setAddress(prev => ({
      ...prev,
      province: provName,
      city: cityName,
      district: distName,
      subdistrict: subdName,
      postal_code: prev.subdistrict !== subdName ? '' : prev.postal_code,
    }))
  }, [provinceId, cityId, districtId, subdistrictId, provinces, cities, districts, subdistricts])

  // Load postal codes by selected village
  useEffect(() => {
    if (!subdistrictId) {
      setPostalCodes([])
      return
    }

    fetchPostalCodesByVillage(subdistrictId)
      .then((items) => {
        const values = Array.isArray(items)
          ? items.map(v => String(v)).filter(Boolean)
          : []
        setPostalCodes(values)
      })
      .catch(() => setPostalCodes([]))
  }, [subdistrictId])

  // Load existing customer for edit and preselect IDs
  useEffect(() => {
    let ignore = false

    ;(async () => {
      if (!isEdit) return

      try {
        const json = await CustomersAPI.show(id)
        const c = unwrapItem(json)

        if (ignore) return

        setName(c?.name || '')
        setEmail(c?.email || '')

        const { country, local } = splitPhoneIfE164(c?.phone || '')
        setPhoneCountry(country)
        setPhone(local || c?.phone || '')

        const addr = Array.isArray(c?.addresses) && c.addresses.length ? c.addresses[0] : {}

        setAddress(prev => ({
          ...prev,
          street: addr.street || '',
          subdistrict: addr.subdistrict || '',
          district: addr.district || '',
          city: addr.city || '',
          province: addr.province || '',
          postal_code: addr.postal_code || '',
          recipient_name: addr.recipient_name || '',
          telephone: addr.telephone || '',
          alias: addr.alias || '',
          is_customer_default: Boolean(addr.is_customer_default ?? true),
          is_seller_default: Boolean(addr.is_seller_default ?? false),
        }))
      } catch (e) {
        if (!ignore) setErr(e.message)
      } finally {
        if (!ignore) setLoading(false)
      }
    })()

    return () => {
      ignore = true
    }
  }, [id, isEdit])

  // Map existing address names -> province/city/district/subdistrict IDs after provinces are loaded
  useEffect(() => {
    let ignore = false

    ;(async () => {
      if (!isEdit || !provinces.length) return
      if (!address.province) return

      try {
        const p = findByName(provinces, 'province', address.province)
        if (!p) return
        if (ignore) return
        setProvinceId(p.id)

        const cs = await fetchCities(p.id)
        if (ignore) return
        setCities(cs)
        const cty = findByName(cs, 'name', address.city)
        if (cty) setCityId(cty.id)

        if (!cty) return
        const ds = await fetchDistricts(cty.id)
        if (ignore) return
        setDistricts(ds)
        const d = findByName(ds, 'name', address.district)
        if (d) setDistrictId(d.id)

        if (!d) return
        const vs = await fetchVillages(d.id)
        if (ignore) return
        setSubdistricts(vs)
        const v = findByName(vs, 'name', address.subdistrict)
        if (v) setSubdistrictId(v.id)
      } catch (_) {
        // silent fail, form still usable
      }
    })()

    return () => {
      ignore = true
    }
  }, [isEdit, provinces.length, address.province, address.city, address.district, address.subdistrict])

  async function onSubmit(e) {
    e.preventDefault()
    setErr('')

    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity()
      return
    }

    const errors = validateCustomer({ name, email, phone })
    if (errors.length > 0) {
      const message = `Please fill required fields:\n- ${errors.join('\n- ')}`
      setErr(message)
      alert(message)
      return
    }

    try {
      setSaving(true)

      const payload = {
        name,
        email,
        phone: combinePhone(phoneCountry, phone),
        ...(hasAnyAddressValue(address)
          ? {
              addresses_attributes: [
                {
                  street: address.street,
                  subdistrict: address.subdistrict,
                  district: address.district,
                  city: address.city,
                  province: address.province,
                  postal_code: address.postal_code,
                  recipient_name: address.recipient_name,
                  telephone: address.telephone,
                  alias: address.alias,
                  is_customer_default: !!address.is_customer_default,
                  is_seller_default: !!address.is_seller_default,
                },
              ],
            }
          : {}),
      }

      if (isEdit) {
        await CustomersAPI.update(id, payload)
        history.push(`${BASE}/customers/${id}`)
      } else {
        const json = await CustomersAPI.create(payload)
        const item = unwrapItem(json)
        history.push(`${BASE}/customers/${item?.id ?? ''}`)
      }
    } catch (e) {
      setErr(e.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div>Loading…</div>

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">{isEdit ? 'Edit' : 'New'} Customer</h2>
        <Link to={`${BASE}/customers`} className="btn btn-link">&larr; Back</Link>
      </div>

      {err && (
        <div className="alert alert-danger" style={{ whiteSpace: 'pre-wrap' }}>
          {err}
        </div>
      )}

      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Name <span className="text-danger">*</span></label>
            <input
              className="form-control"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>

          <div className="form-group col-md-6">
            <label>Email <span className="text-danger">*</span></label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label>Phone <span className="text-danger">*</span></label>
          <div className="input-group">
            <div className="input-group-prepend">
              <select
                className="form-control"
                value={phoneCountry}
                onChange={e => setPhoneCountry(e.target.value)}
                style={{ maxWidth: 180 }}
                aria-label="Phone country code"
                required
              >
                {PHONE_COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <input
              className="form-control"
              placeholder="e.g. 8123456789"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              required
            />
          </div>
          <small className="form-text text-muted">
            Saved as E.164 (e.g. +62812…)
          </small>
        </div>

        <hr />
        <h5 className="mb-3">Address Information</h5>

        <div className="form-group">
          <label>Street</label>
          <input
            className="form-control"
            value={address.street}
            onChange={e => updateAddressField('street', e.target.value)}
          />
        </div>

        {regionSourceFailed && (
          <div className="alert alert-warning">
            Region service is temporarily unavailable. You can still save customer without address.
          </div>
        )}

        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Province</label>
            <select
              className="form-control"
              value={provinceId}
              onChange={e => {
                setProvinceId(e.target.value)
                setCityId('')
                setDistrictId('')
                setSubdistrictId('')
                setCities([])
                setDistricts([])
                setSubdistricts([])
                setPostalCodes([])
              }}
              disabled={regionSourceFailed}
            >
              <option value="">
                {regionSourceFailed ? 'Region service unavailable' : 'Select Province…'}
              </option>
              {provinces.map(p => (
                <option key={p.id} value={p.id}>
                  {p.province}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group col-md-6">
            <label>City/Regency</label>
            <select
              className="form-control"
              value={cityId}
              onChange={e => {
                setCityId(e.target.value)
                setDistrictId('')
                setSubdistrictId('')
                setDistricts([])
                setSubdistricts([])
                setPostalCodes([])
              }}
              disabled={regionSourceFailed || !provinceId}
            >
              <option value="">
                {provinceId ? 'Select City/Regency…' : 'Select Province first'}
              </option>
              {cities.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-md-6">
            <label>District (Kecamatan)</label>
            <select
              className="form-control"
              value={districtId}
              onChange={e => {
                setDistrictId(e.target.value)
                setSubdistrictId('')
                setSubdistricts([])
                setPostalCodes([])
              }}
              disabled={regionSourceFailed || !cityId}
            >
              <option value="">
                {cityId ? 'Select District…' : 'Select City first'}
              </option>
              {districts.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group col-md-6">
            <label>Subdistrict (Kelurahan/Desa)</label>
            <select
              className="form-control"
              value={subdistrictId}
              onChange={e => {
                setSubdistrictId(e.target.value)
                setPostalCodes([])
              }}
              disabled={regionSourceFailed || !districtId}
            >
              <option value="">
                {districtId ? 'Select Subdistrict…' : 'Select District first'}
              </option>
              {subdistricts.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group col-md-6">
            <label>Postal Code</label>
            {postalCodes.length > 0 ? (
              <select
                className="form-control"
                value={address.postal_code}
                onChange={e => updateAddressField('postal_code', e.target.value)}
                disabled={regionSourceFailed || !subdistrictId}
              >
                <option value="">Select Postal Code…</option>
                {postalCodes.map(pc => (
                  <option key={pc} value={pc}>
                    {pc}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="form-control"
                placeholder="Type postal code"
                value={address.postal_code}
                onChange={e => updateAddressField('postal_code', e.target.value)}
              />
            )}
          </div>

          <div className="form-group col-md-3">
            <label>Recipient Name</label>
            <input
              className="form-control"
              value={address.recipient_name}
              onChange={e => updateAddressField('recipient_name', e.target.value)}
            />
          </div>

          <div className="form-group col-md-3">
            <label>Telephone</label>
            <input
              className="form-control"
              value={address.telephone}
              onChange={e => updateAddressField('telephone', e.target.value)}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Alias (e.g., Home, Office)</label>
          <input
            className="form-control"
            value={address.alias}
            onChange={e => updateAddressField('alias', e.target.value)}
          />
        </div>

        <div className="form-check mb-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="is_customer_default"
            checked={!!address.is_customer_default}
            onChange={e => updateAddressField('is_customer_default', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="is_customer_default">
            Use as default shipping address
          </label>
        </div>

        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="is_seller_default"
            checked={!!address.is_seller_default}
            onChange={e => updateAddressField('is_seller_default', e.target.checked)}
          />
          <label className="form-check-label" htmlFor="is_seller_default">
            Use as default pickup address
          </label>
        </div>

        <hr />
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Save Customer'}
        </button>
      </form>
    </div>
  )
}
